import {
  ArrowRight,
  Calculator,
  Check,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  FileSearch,
  Layers3,
  MessageCircleMore,
  Ruler,
  ShieldCheck,
  Sparkles,
  UsersRound,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { apiFetch } from "@contractor-quoting/api-client";
import { Badge, Card } from "@contractor-quoting/shared-ui";
import { LandingChatPrompt } from "../components/LandingChatPrompt.js";
import { Shell } from "../components/Shell.js";

const customerFriction = [
  "Unsure which measurements actually matter",
  "No clear way to express material preferences",
  "Anxiety about sharing a project before it feels ready",
];

const contractorFriction = [
  "Hours spent figuring out which enquiries are serious",
  "Project details scattered across calls, texts, and photos",
  "Price discussions that start before everyone agrees on the work",
];

const planningSteps = [
  {
    number: "01",
    title: "Answer a few helpful questions",
    text: "A friendly guide walks the customer through each room and explains what information the contractor needs.",
  },
  {
    number: "02",
    title: "See the whole project",
    text: "Room sizes, flooring preferences, photos, and a simple room sketch come together in one easy summary.",
  },
  {
    number: "03",
    title: "Get a realistic starting range",
    text: "The expected price is based on the project details and the contractor’s own rates—not a number invented by the chat guide.",
  },
  {
    number: "04",
    title: "Share it when you are ready",
    text: "Nothing goes to the contractor until the customer reviews the details and chooses to send the project.",
  },
];

const productSurfaces = [
  {
    Icon: MessageCircleMore,
    title: "Step-by-step project help",
    text: "A simple conversation that works well on a phone, shows what is left, and lets customers stop and return later.",
  },
  {
    Icon: Calculator,
    title: "A clear starting price",
    text: "Customers see a useful price range based on their rooms, choices, and the contractor’s rates—with no mystery math.",
  },
  {
    Icon: UsersRound,
    title: "Everything in one place",
    text: "Contractors can review the customer, rooms, photos, expected price, notes, next steps, and later changes together.",
  },
];

function CheckList({ items }: { items: string[] }) {
  return (
    <ul className="mt-6 space-y-4">
      {items.map((item) => (
        <li className="flex items-start gap-3 text-stone-700" key={item}>
          <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-800">
            <Check size={14} strokeWidth={3} />
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function LandingPage() {
  return (
    <Shell dark>
      <main className="overflow-hidden">
        <section className="relative flex min-h-[calc(100svh-73px)] items-center bg-[#071713] px-4 py-16 text-white sm:px-6 sm:py-20">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-32 top-12 h-96 w-96 rounded-full bg-emerald-600/15 blur-3xl" />
            <div className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-orange-500/12 blur-3xl" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:linear-gradient(to_bottom,black,transparent)]" />
          </div>
          <div className="relative mx-auto flex w-full max-w-5xl flex-col items-center text-center">
            <Badge className="mb-7 border border-orange-400/30 bg-orange-500/10 px-4 py-2 text-xs uppercase tracking-[0.2em] text-orange-200">
              Price. Plan. Build.
            </Badge>
            <h1 className="text-balance text-[2.85rem] font-black leading-[0.98] tracking-[-0.055em] text-white sm:text-6xl lg:text-[4.65rem]">
              What can Mezoniq help you plan?
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-stone-300 sm:text-xl">
              Describe the project in your own words. Mezoniq will help turn the idea into the
              details, measurements, and decisions a contractor needs.
            </p>
            <div className="w-full max-w-4xl text-left">
              <LandingChatPrompt dark />
            </div>
          </div>
        </section>

        <section
          aria-label="Mezoniq — Price. Plan. Build."
          className="relative min-h-[34rem] bg-[#f7f2ec] bg-[url('/brand/mziq-banner.png')] bg-cover bg-center bg-no-repeat sm:min-h-[42rem] lg:min-h-[52rem] lg:bg-fixed"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#f5f5f0]" />
        </section>

        <section className="relative z-10 -mt-24 px-4 pb-16 sm:px-6 lg:-mt-36 lg:pb-24">
          <div className="mx-auto max-w-7xl rounded-t-[2.5rem] border border-b-0 border-stone-200 bg-[#f5f5f0] px-5 pb-2 pt-10 shadow-[0_-24px_70px_rgba(21,44,38,0.1)] sm:px-8 lg:px-12 lg:pt-14">
            <div className="mx-auto max-w-4xl text-center">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-orange-600">
                From an idea to a useful project
              </p>
              <h2 className="mt-4 text-4xl font-black tracking-tight text-emerald-950 sm:text-5xl">
                Start naturally. Arrive at the contractor conversation prepared.
              </h2>
              <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-stone-600">
                Mezoniq gives customers a comfortable place to think through the work and gives
                contractors a clear, structured project they can actually review.
              </p>
            </div>

            <div className="relative mt-10 overflow-hidden rounded-[2rem] border border-emerald-900/10 bg-white shadow-[0_25px_90px_rgba(29,65,57,0.12)] lg:mt-14">
              <div className="grid lg:grid-cols-[1fr_auto_1fr]">
                <div className="p-7 sm:p-9 lg:p-11">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">
                    The customer is thinking
                  </p>
                  <p className="mt-4 text-2xl font-bold leading-tight text-emerald-950 sm:text-3xl">
                    “I don’t want to waste their time—but I don’t know what they need.”
                  </p>
                </div>
                <div className="grid place-items-center bg-emerald-950 px-6 py-5 text-center text-white lg:w-48">
                  <div>
                    <Layers3 className="mx-auto text-emerald-300" size={28} />
                    <p className="mt-3 text-sm font-bold">One clear project plan</p>
                  </div>
                </div>
                <div className="p-7 sm:p-9 lg:p-11">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">
                    The contractor is thinking
                  </p>
                  <p className="mt-4 text-2xl font-bold leading-tight text-emerald-950 sm:text-3xl">
                    “I can help—but first I need to know whether this project is real.”
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-stone-200 bg-white" id="the-problem">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
            <div className="max-w-3xl">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-emerald-700">
                The problem
              </p>
              <h2 className="mt-4 text-4xl font-black tracking-tight text-emerald-950 sm:text-5xl">
                Good projects stall in the gap between interest and useful information.
              </h2>
            </div>
            <div className="mt-10 grid gap-5 lg:grid-cols-2">
              <Card className="rounded-[1.75rem] border-amber-200 bg-amber-50/70 p-7 sm:p-9">
                <Ruler className="text-amber-700" size={30} />
                <h3 className="mt-5 text-2xl font-bold text-stone-950">For customers</h3>
                <p className="mt-3 leading-7 text-stone-600">
                  The desire is real, but the path from “we should redo this room” to a credible
                  project feels confusing, awkward, and high stakes.
                </p>
                <CheckList items={customerFriction} />
              </Card>
              <Card className="rounded-[1.75rem] border-emerald-200 bg-emerald-50/70 p-7 sm:p-9">
                <Clock3 className="text-emerald-800" size={30} />
                <h3 className="mt-5 text-2xl font-bold text-emerald-950">For contractors</h3>
                <p className="mt-3 leading-7 text-stone-600">
                  Every vague enquiry takes time, yet room sizes, preferences, photos, and budget
                  expectations rarely arrive together.
                </p>
                <CheckList items={contractorFriction} />
              </Card>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <div className="grid items-start gap-12 lg:grid-cols-[0.8fr_1.2fr]">
            <div className="lg:sticky lg:top-24">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-emerald-700">
                The ideal solution
              </p>
              <h2 className="mt-4 text-4xl font-black tracking-tight text-emerald-950 sm:text-5xl">
                Imagine arriving at the first real conversation already aligned.
              </h2>
              <p className="mt-5 text-lg leading-8 text-stone-600">
                The customer understands the work and likely budget. The contractor sees the useful
                details in one place. Both know exactly what happens next.
              </p>
            </div>
            <div className="space-y-4">
              {planningSteps.map((step) => (
                <article
                  className="grid gap-4 rounded-3xl border border-stone-200 bg-white p-6 shadow-sm sm:grid-cols-[4rem_1fr] sm:p-7"
                  key={step.number}
                >
                  <span className="text-3xl font-black text-emerald-200">{step.number}</span>
                  <div>
                    <h3 className="text-xl font-bold text-emerald-950">{step.title}</h3>
                    <p className="mt-2 leading-7 text-stone-600">{step.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-stone-900 text-white">
          <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_1.1fr] lg:py-24">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-amber-300">
                The catch
              </p>
              <h2 className="mt-4 text-4xl font-black tracking-tight sm:text-5xl">
                Most “easy quoting” tools put the same confusion on a screen.
              </h2>
              <p className="mt-5 text-lg leading-8 text-stone-300">
                A generic form cannot guide a nervous homeowner. A lead marketplace cannot protect a
                contractor’s way of working. And a clever chatbot that does not organize the details
                leaves everyone with a long conversation instead of a useful plan.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
              {[
                [FileSearch, "Forms collect answers", "They do not help people know what to say."],
                [
                  CircleDollarSign,
                  "Calculators show a number",
                  "They rarely explain what is included.",
                ],
                [Sparkles, "AI can sound confident", "A confident answer can still be a guess."],
              ].map(([Icon, title, text]) => {
                const ItemIcon = Icon as typeof FileSearch;
                return (
                  <div className="flex gap-4 rounded-2xl bg-white/7 p-5" key={String(title)}>
                    <ItemIcon className="shrink-0 text-amber-300" size={24} />
                    <div>
                      <h3 className="font-bold">{String(title)}</h3>
                      <p className="mt-1 text-sm leading-6 text-stone-400">{String(text)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24" id="how-it-works">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-emerald-700">
              Our solution
            </p>
            <h2 className="mt-4 text-4xl font-black tracking-tight text-emerald-950 sm:text-5xl">
              The missing middle between “I’m interested” and “let’s review the project.”
            </h2>
            <p className="mt-5 text-lg leading-8 text-stone-600">
              Mezoniq walks customers through the questions contractors usually have to ask later.
              It turns those answers into a clear project summary and starting price, then gives the
              contractor one place to review it all.
            </p>
          </div>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {productSurfaces.map(({ Icon, title, text }) => (
              <Card className="rounded-[1.75rem] p-7" key={title}>
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-emerald-100 text-emerald-800">
                  <Icon size={24} />
                </span>
                <h3 className="mt-6 text-xl font-bold text-emerald-950">{title}</h3>
                <p className="mt-3 leading-7 text-stone-600">{text}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="border-y border-emerald-900/10 bg-emerald-950 text-white">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
            <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-emerald-300">
                  One project’s story
                </p>
                <h2 className="mt-4 text-4xl font-black tracking-tight sm:text-5xl">
                  From a Sunday idea to a useful Monday conversation.
                </h2>
              </div>
              <div className="space-y-8 border-l border-emerald-700 pl-6 sm:pl-10">
                <div>
                  <p className="font-bold text-emerald-300">Sunday, 7:18 p.m.</p>
                  <p className="mt-2 text-lg leading-8 text-emerald-50">
                    Maya opens her flooring contractor’s link from the couch. The guide helps her
                    measure two rooms, account for a closet, and choose “durable, mid-market
                    vinyl”—without asking her to know the exact product.
                  </p>
                </div>
                <div>
                  <p className="font-bold text-emerald-300">Sunday, 7:31 p.m.</p>
                  <p className="mt-2 text-lg leading-8 text-emerald-50">
                    She sees a simple room sketch, reviews an expected price range, signs in, and
                    decides the project is ready to share. Only then does she send it.
                  </p>
                </div>
                <div>
                  <p className="font-bold text-emerald-300">Monday, 8:04 a.m.</p>
                  <p className="mt-2 text-lg leading-8 text-emerald-50">
                    The contractor opens one clear summary with the measurements, preferences,
                    reference photos, material needed, and expected costs for the next conversation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24" id="for-contractors">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="rounded-[2rem] border-emerald-200 bg-emerald-50 p-7 sm:p-10">
              <ClipboardCheck className="text-emerald-800" size={32} />
              <p className="mt-7 text-sm font-bold uppercase tracking-[0.2em] text-emerald-700">
                We are for
              </p>
              <h2 className="mt-3 text-3xl font-black text-emerald-950">
                Contractors who compete on clarity, trust, and a professional process.
              </h2>
              <p className="mt-5 leading-7 text-stone-700">
                And customers who want to do the thinking required for a useful conversation—not
                chase a suspiciously instant price.
              </p>
            </Card>
            <Card className="rounded-[2rem] border-stone-300 bg-white p-7 sm:p-10">
              <X className="text-stone-500" size={32} />
              <p className="mt-7 text-sm font-bold uppercase tracking-[0.2em] text-stone-500">
                We are not
              </p>
              <h2 className="mt-3 text-3xl font-black text-stone-900">
                A race-to-the-bottom lead marketplace or an instant-price gimmick.
              </h2>
              <p className="mt-5 leading-7 text-stone-600">
                The first price is a helpful starting range, not a final quote. Room sketches are a
                guide, not building plans. The contractor still reviews the project and makes the
                final call.
              </p>
            </Card>
          </div>
          <div className="mt-16 rounded-[2rem] bg-amber-300 px-6 py-10 text-center sm:px-10 sm:py-14">
            <ShieldCheck className="mx-auto text-emerald-950" size={34} />
            <p className="mx-auto mt-5 max-w-4xl text-3xl font-black leading-tight tracking-tight text-emerald-950 sm:text-5xl">
              We believe the best technology does not replace contractor judgment. It makes that
              judgment easier to apply where it matters.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <a
                className="inline-flex min-h-14 items-center justify-center gap-2 rounded-2xl bg-emerald-950 px-6 font-bold text-white hover:bg-emerald-900"
                href="#the-problem"
              >
                Explore the approach <ArrowRight size={18} />
              </a>
              <Link
                className="inline-flex min-h-14 items-center justify-center rounded-2xl border border-emerald-950/20 bg-white/70 px-6 font-bold text-emerald-950 hover:bg-white"
                to="/login?returnTo=/contractor/projects"
              >
                Contractor sign in
              </Link>
            </div>
          </div>
        </section>
      </main>
    </Shell>
  );
}

interface ContractorPublic {
  contractor: {
    slug: string;
    name: string;
    description: string;
    brandPrimary: string;
    serviceArea: string | null;
  };
  supportedProjectTypes: string[];
}

export function ContractorLandingPage() {
  const { contractorSlug = "" } = useParams();
  const [data, setData] = useState<ContractorPublic | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    void apiFetch<ContractorPublic>(`/v1/public/contractors/${encodeURIComponent(contractorSlug)}`)
      .then(setData)
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : "Contractor unavailable"),
      );
  }, [contractorSlug]);
  return (
    <Shell>
      <main className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
        {error ? (
          <Card>
            <h1 className="text-2xl font-bold">This quote link is unavailable.</h1>
            <p className="mt-2 text-stone-600">{error}</p>
          </Card>
        ) : null}
        {!data && !error ? <p>Loading contractor…</p> : null}
        {data ? (
          <div className="grid gap-10 lg:grid-cols-[1fr_360px]">
            <div>
              <p
                className="text-sm font-bold uppercase tracking-widest"
                style={{ color: data.contractor.brandPrimary }}
              >
                Project planning with
              </p>
              <h1 className="mt-3 text-5xl font-black text-emerald-950">{data.contractor.name}</h1>
              <p className="mt-6 text-xl leading-8 text-stone-600">
                {data.contractor.description ||
                  "Tell us about your project and we’ll prepare a starting price range for the contractor to review."}
              </p>
              {data.contractor.serviceArea ? (
                <p className="mt-4 font-semibold text-stone-700">
                  Serving {data.contractor.serviceArea}
                </p>
              ) : null}
            </div>
            <Card>
              <h2 className="text-xl font-bold">Start a flooring project</h2>
              <p className="mt-2 text-stone-600">
                Start without creating an account. Sign in only when you are ready to save your
                project and price range.
              </p>
              <Link
                className="mt-6 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-800 px-5 py-3 font-semibold text-white transition hover:bg-emerald-900"
                to={`/chat/${data.contractor.slug}`}
              >
                Start with the project guide <ArrowRight size={18} />
              </Link>
            </Card>
          </div>
        ) : null}
      </main>
    </Shell>
  );
}
